// Bausteine der Bildschirm-Überwachung: Live-Screenshot mit Anforderung, Alarm-Abzeichen, Alarmliste.
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { api } from '../api/client';
import { mk, useDeviceMonitoring, useScreenshotRequest, type Alert, type OverviewItem, type Screenshot } from '../api/monitoring';
import { localeOf } from '../i18n';
import { Icon } from './Icon';
import { useToast } from './toast';
import { formatDateTime, relativeTime } from './ui';

/** Abzeichen auf Kacheln: Fehler/offline-Alarm und Stummschaltung. */
export function AlertBadges({ item }: { item: OverviewItem | undefined }) {
  const { t } = useTranslation();
  if (!item || (!item.muted && item.alert?.kind !== 'error')) return null;
  return (
    <span className="shot-badges">
      {item.alert?.kind === 'error' && <span className="pill error" title={item.alert.code ?? ''}>{t('monitoring.badge.error')}</span>}
      {item.muted && <span className="pill neutral plain" title={t('monitoring.screen.muteHint')}>{t('monitoring.badge.muted')}</span>}
    </span>
  );
}

/** Screenshot-Bild mit Zeitstempel (oben links), z. B. auf Kacheln. */
export function ScreenshotImage({ shot, compact }: { shot: Screenshot; compact?: boolean }) {
  const { t, i18n } = useTranslation();
  const locale = localeOf(i18n.language);
  return (
    <>
      <img className="shot-img" src={shot.url} alt={t('monitoring.screen.screenshot')} loading="lazy" />
      <span className="shot-time" title={formatDateTime(shot.capturedAt, locale)}>
        <Icon name="camera" />{compact ? relativeTime(shot.capturedAt, locale) : formatDateTime(shot.capturedAt, locale)}
      </span>
    </>
  );
}

/** Register „Überwachung“ im Bildschirm-Detail: Live-Screenshot, frühere Bilder, Stummschaltung, Alarme. */
export function DeviceMonitoringPanel({ deviceId, online, canCommand, canManage }: { deviceId: string; online: boolean; canCommand: boolean; canManage: boolean }) {
  const { t, i18n } = useTranslation();
  const locale = localeOf(i18n.language);
  const qc = useQueryClient();
  const toast = useToast();
  const { data: m } = useDeviceMonitoring(deviceId);
  const req = useScreenshotRequest(deviceId);
  const [shown, setShown] = useState<string | null>(null);
  const shots = m?.screenshots ?? [];
  const current = shots.find((s) => s.id === shown) ?? shots[0] ?? null;
  const last = req.result ?? m?.lastScreenshotCommand ?? null;
  const waiting = req.busy || (!!last && (last.status === 'pending' || last.status === 'sent'));

  const refresh = () => Promise.all([qc.invalidateQueries({ queryKey: mk.device(deviceId) }), qc.invalidateQueries({ queryKey: mk.overview })]);
  const mute = async (hours: number | null) => {
    try {
      await api.put(`/devices/${deviceId}/alert-mute`, { mutedUntil: hours ? new Date(Date.now() + hours * 3600_000).toISOString() : null });
      await refresh();
      toast.show(t('monitoring.screen.mutedToast'));
    } catch (e) {
      toast.error(e);
    }
  };
  const unmute = async () => {
    try {
      await api.del(`/devices/${deviceId}/alert-mute`);
      await refresh();
    } catch (e) {
      toast.error(e);
    }
  };
  const remove = async (s: Screenshot) => {
    if (!window.confirm(t('monitoring.screen.deleteConfirm'))) return;
    try {
      await api.del(`/devices/${deviceId}/screenshots/${s.id}`);
      setShown(null);
      await refresh();
    } catch (e) {
      toast.error(e);
    }
  };

  let note: string | null = null;
  if (waiting) note = online ? t('monitoring.screen.requesting') : t('monitoring.screen.offlineHint');
  else if (last?.status === 'failed') note = t('monitoring.screen.failed', { msg: last.message ?? '–' });
  else if (last?.status === 'expired' || (req.result && (req.result.status === 'pending' || req.result.status === 'sent'))) note = t('monitoring.screen.timeout');

  return (
    <div className="grid-2">
      <div className="card col">
        <div className="row">
          <h2 style={{ margin: 0 }} className="grow">{t('monitoring.screen.screenshot')}</h2>
          {canCommand && (
            <button className="btn primary" onClick={() => void req.request()} disabled={waiting} data-testid="request-screenshot">
              <Icon name="camera" />{waiting ? t('monitoring.screen.requestingShort') : t('monitoring.screen.request')}
            </button>
          )}
        </div>
        <div className="tv shot">
          {current ? (
            <a href={current.url} target="_blank" rel="noopener" title={t('monitoring.screen.openFull')} style={{ position: 'absolute', inset: 0 }}>
              <ScreenshotImage shot={current} />
            </a>
          ) : <span className="small">{t('monitoring.screen.none')}</span>}
          {waiting && <span className="shot-busy" role="status"><Icon name="refresh" /></span>}
        </div>
        {note && <div className={`hint${last?.status === 'failed' || last?.status === 'expired' ? ' warn' : ''}`} role="status">{note}</div>}
        {current && (
          <div className="row small muted">
            <span className="grow">
              {t('monitoring.screen.taken', { when: formatDateTime(current.capturedAt, locale) })} · {current.source === 'schedule' ? t('monitoring.screen.auto') : t('monitoring.screen.manual')}
              {current.width && current.height ? ` · ${current.width} × ${current.height}` : ''}
            </span>
            {canManage && <button className="btn sm ghost icon danger" aria-label={t('monitoring.screen.delete')} onClick={() => void remove(current)}><Icon name="trash" /></button>}
          </div>
        )}
        {shots.length > 1 && (
          <>
            <div className="small muted">{t('monitoring.screen.history')}</div>
            <div className="shot-strip">
              {shots.map((s) => (
                <button key={s.id} className={`shot-thumb${current?.id === s.id ? ' on' : ''}`} onClick={() => setShown(s.id)} title={formatDateTime(s.capturedAt, locale)}>
                  <img src={s.url} alt="" loading="lazy" />
                  <span>{new Date(s.capturedAt).toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' })}</span>
                </button>
              ))}
            </div>
          </>
        )}
        <div className="small muted">
          {m && m.screenshotIntervalMinutes > 0 ? `${t('monitoring.screen.autoEvery', { count: m.screenshotIntervalMinutes })} · ` : ''}
          {t('monitoring.screen.retention', { count: m?.screenshotRetentionHours ?? 24 })}
        </div>
      </div>

      <div className="card col" style={{ alignContent: 'start' }}>
        <div className="row">
          <h2 style={{ margin: 0 }} className="grow">{t('monitoring.screen.alerts')}</h2>
          {m?.mute ? <span className="pill neutral plain">{m.mute.mutedUntil ? t('monitoring.screen.mutedUntil', { when: formatDateTime(m.mute.mutedUntil, locale) }) : t('monitoring.badge.muted')}</span> : null}
        </div>
        {canManage && (
          <div className="row" style={{ flexWrap: 'wrap' }}>
            {m?.mute ? (
              <button className="btn sm" onClick={() => void unmute()}>{t('monitoring.screen.unmute')}</button>
            ) : (
              <>
                <span className="small">{t('monitoring.screen.mute')}:</span>
                <button className="btn sm" onClick={() => void mute(1)}>{t('monitoring.screen.mute1h')}</button>
                <button className="btn sm" onClick={() => void mute(24)}>{t('monitoring.screen.mute1d')}</button>
                <button className="btn sm" onClick={() => void mute(null)}>{t('monitoring.screen.muteForever')}</button>
              </>
            )}
          </div>
        )}
        <div className="small muted">{t('monitoring.screen.muteHint')}</div>
        <AlertList alerts={m?.alerts ?? []} showDevice={false} canManage={canManage} onChanged={refresh} />
      </div>
    </div>
  );
}

/** Alarmtabelle (Verlauf). */
export function AlertList({ alerts, showDevice, canManage, onChanged }: { alerts: Alert[]; showDevice: boolean; canManage: boolean; onChanged: () => unknown }) {
  const { t, i18n } = useTranslation();
  const locale = localeOf(i18n.language);
  const toast = useToast();
  if (!alerts.length) return <div className="muted">{t('monitoring.noAlerts')}</div>;
  const resolve = async (a: Alert) => {
    try {
      await api.post(`/monitoring/alerts/${a.id}/resolve`);
      await onChanged();
    } catch (e) {
      toast.error(e);
    }
  };
  return (
    <div className="scroll">
      <table className="list">
        <thead><tr>
          {showDevice && <th>{t('monitoring.col.screen')}</th>}
          <th>{t('monitoring.col.kind')}</th><th>{t('monitoring.col.since')}</th><th>{t('monitoring.col.status')}</th><th>{t('monitoring.col.mail')}</th><th />
        </tr></thead>
        <tbody>
          {alerts.map((a) => (
            <tr key={a.id}>
              {showDevice && <td><Link to={`/screens/${a.deviceId}`}><b>{a.deviceName}</b></Link></td>}
              <td>
                <span className={`pill ${a.kind === 'error' ? 'error' : 'offline'}`}>{t(`monitoring.kind.${a.kind}`)}</span>
                {a.kind === 'error' && <div className="small muted">{a.code ? t(`monitoring.code.${a.code}`, { defaultValue: a.code }) : ''}{a.occurrences > 1 ? ` · ${a.occurrences}×` : ''}</div>}
              </td>
              <td title={formatDateTime(a.since, locale)}>{formatDateTime(a.since, locale)}</td>
              <td>
                {a.status === 'open'
                  ? <span className="pill warn">{t('monitoring.status.open')}</span>
                  : <span className="small">{t(`monitoring.res.${a.resolution ?? 'manual'}`)}<br /><span className="muted">{formatDateTime(a.resolvedAt, locale)}</span></span>}
              </td>
              <td className="small muted">{a.muted ? `${t('monitoring.badge.muted')}` : a.notifiedAt ? `✓ ${t('monitoring.mailed')}` : '–'}</td>
              <td>{canManage && a.status === 'open' && <button className="btn sm" onClick={() => void resolve(a)}>{t('monitoring.resolve')}</button>}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
